import { useQuery } from '@tanstack/react-query';
import { CONTEXT_SECTIONS, CONTEXT_SECTION_TITLES, type ContextSection } from '@lixionary-jira/domain';
import { Link, useParams } from 'react-router';
import { api, useProject } from '../../api/hooks';
import type { ContextVersionView } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Alert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Markdown } from '../../ui/Markdown';
import { ActorName, Card, KeyValue, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

type VersionDetail = ContextVersionView & {
  title: string;
  predecessorNumber: number | null;
  currentNumber: number | null;
  isCurrent: boolean;
};

/** Immutable historical context (PRJ-05, INT-03). */
export function ContextVersionPage() {
  const { key, section, versionId } = useParams();
  const project = useProject(key);
  const valid = (CONTEXT_SECTIONS as readonly string[]).includes(section ?? '');
  const title = valid ? CONTEXT_SECTION_TITLES[section as ContextSection] : '';
  const q = useQuery({
    queryKey: ['context-version', key, section, versionId],
    queryFn: () => api.get<VersionDetail>(`/projects/${key}/context/${section}/versions/${versionId}`),
    enabled: valid,
  });
  usePageTitle(project.data && q.data ? `${project.data.name} · ${title} · Version ${q.data.number}` : null);
  useCrumbs([
    { label: project.data?.name ?? key ?? '', to: `/projects/${key}` },
    { label: title, to: `/projects/${key}/context/${section}` },
    { label: `Version ${q.data?.number ?? versionId}` },
  ]);
  if (!valid) return <PageUnavailable />;
  return (
    <div className="page">
      <QueryView query={q}>
        {(v) => (
          <>
            <PageHeader title={`${title} · Version ${v.number}`} />
            {!v.isCurrent && (
              <Alert tone="info">
                This is a historical version.{' '}
                <Link to={`/projects/${key}/context/${section}`}>
                  View current version v{v.currentNumber}
                </Link>
              </Alert>
            )}
            <div className="split">
              <Card>
                <Markdown>{v.body}</Markdown>
              </Card>
              <Card title="Version" tight>
                <KeyValue
                  items={[
                    ['Author', <ActorName key="a" actor={v.author} />],
                    ['Saved', <Timestamp key="t" value={v.createdAt} />],
                    ['Summary', v.changeSummary],
                    [
                      'Predecessor',
                      v.predecessorNumber ? (
                        <Link
                          key="p"
                          to={`/projects/${key}/context/${section}/versions/${v.predecessorNumber}`}
                          className="mono"
                        >
                          v{v.predecessorNumber}
                        </Link>
                      ) : (
                        'None'
                      ),
                    ],
                    [
                      'ID',
                      <span key="i" className="mono small">
                        {v.id}
                      </span>,
                    ],
                  ]}
                />
              </Card>
            </div>
          </>
        )}
      </QueryView>
    </div>
  );
}
